import React, { useMemo, useState, useEffect, useCallback } from 'react';

import { Check, ClockAlert, Pencil, RefreshCw, X } from 'lucide-react';
import { useQuery, useMutation, queryCache } from 'react-query';
import Tooltip from 'react-tooltip';
import { DotBricksLoading, Toast } from 'src/common/components';

import { fetchWLDevices, refreshTV, updateDeviceNickname } from './api';
import { DeviceStatus, DeviceType, WLDevice } from './types';
import {
  formatExactDatetime,
  formatLastSeen,
  formatOutdatedFor,
  getLatestVersion,
  groupDevices,
  isOutdated,
  latestVersionFor,
} from './utils';

const REFETCH_INTERVAL_S = 60;
const GROUPS_PER_PAGE = 10;

const STATUS_LABEL: Record<DeviceStatus, string> = {
  online: 'Online',
  warning: 'Warning',
  offline: 'Offline',
};

const STATUS_CLASSES: Record<DeviceStatus, string> = {
  online: 'bg-green-100 text-green-800',
  warning: 'bg-yellow-100 text-yellow-800',
  offline: 'bg-red-100 text-red-800',
};

const DEVICE_TYPE_LABEL: Record<string, string> = {
  TV: 'TV',
  KIOSK: 'Kiosk',
  COFFEE_ROOM: 'Coffee Room',
};

const REFRESHABLE_DEVICE_TYPES: DeviceType[] = ['TV', 'COFFEE_ROOM'];

const OS_LABEL: Record<string, string> = {
  darwin: 'Mac',
  win32: 'Windows',
  linux: 'Linux',
};

const FILTER_ALL = 'all' as const;
const FILTER_OUTDATED = 'outdated' as const;
type StatusFilter = DeviceStatus | typeof FILTER_ALL | typeof FILTER_OUTDATED;

const STATUS_BADGE_CONFIG: { key: DeviceStatus; bg: string; text: string; dot: string }[] = [
  { key: 'online', bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
  { key: 'warning', bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
  { key: 'offline', bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
];

const Check16 = (): JSX.Element => <Check size={14} />;
const X16 = (): JSX.Element => <X size={14} />;
const Pencil16 = (): JSX.Element => <Pencil size={14} />;
const RefreshCw16 = (): JSX.Element => <RefreshCw size={14} />;
const ClockAlert14 = (): JSX.Element => <ClockAlert size={14} />;

// Kiosk devices don't require audio checks, so backend `warning` (audio-driven) is treated as online
const getEffectiveStatus = (device: WLDevice): DeviceStatus =>
  device.device_type === 'KIOSK' && device.status === 'warning' ? 'online' : device.status;

const NicknameCell: React.FC<{ device: WLDevice }> = ({ device }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(device.nickname);

  const [mutate, { isLoading }] = useMutation(
    (nickname: string) => updateDeviceNickname(device.id, nickname),
    {
      onSuccess: (res) => {
        setValue(res.data.nickname);
        setEditing(false);
        queryCache.invalidateQueries('wlDevices');
      },
    }
  );

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5 group">
        <span>{value || '—'}</span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="group-hover:opacity-100 hover:text-gray-600 text-gray-400 transition-opacity opacity-0"
          aria-label="Edit nickname"
        >
          <Pencil16 />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        className="border rounded px-1.5 py-0.5 text-sm w-64 focus:outline-none focus:ring-1 focus:ring-primary-2.0"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') mutate(value);
          if (e.key === 'Escape') {
            setValue(device.nickname);
            setEditing(false);
          }
        }}
        autoFocus
      />
      <button
        type="button"
        onClick={() => mutate(value)}
        disabled={isLoading}
        className="hover:text-green-700 text-green-600"
        aria-label="Save"
      >
        <Check16 />
      </button>
      <button
        type="button"
        onClick={() => {
          setValue(device.nickname);
          setEditing(false);
        }}
        className="hover:text-red-600 text-red-500"
        aria-label="Cancel"
      >
        <X16 />
      </button>
    </div>
  );
};

const DeviceRow: React.FC<{ device: WLDevice; latestVersion: string | null }> = ({
  device,
  latestVersion,
}) => {
  const effectiveStatus = getEffectiveStatus(device);
  const isOffline = effectiveStatus === 'offline';
  const canRefresh = REFRESHABLE_DEVICE_TYPES.includes(device.device_type);
  const isKiosk = device.device_type === 'KIOSK';
  const outdated = isOutdated(device, latestVersion);

  const [mutateRefresh, { isLoading: isRefreshing }] = useMutation(refreshTV, {
    onSuccess: () => {
      Toast.success('Refresh sent to device.');
    },
    onError: () => {
      Toast.error('Could not send refresh.');
    },
  });

  return (
    <tr className="hover:bg-gray-50 border-t border-gray-200">
      <td className="w-24 px-4 py-2 text-sm">
        <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded font-mono">
          {DEVICE_TYPE_LABEL[device.device_type] ?? device.device_type}
        </span>
      </td>
      <td className="px-4 py-2 text-sm text-gray-900">
        <NicknameCell device={device} />
      </td>
      <td className="w-24 px-4 py-2 text-sm">
        <span
          className={`inline-block text-xs px-2 py-0.5 rounded font-medium ${STATUS_CLASSES[effectiveStatus]}`}
        >
          {STATUS_LABEL[effectiveStatus]}
        </span>
      </td>
      <td className="w-28 px-4 py-2 text-sm text-gray-600">
        <span data-for={`last-seen-${device.id}`} data-tip className="cursor-default">
          {formatLastSeen(device.last_heartbeat_at)}
        </span>
        <Tooltip id={`last-seen-${device.id}`} effect="solid">
          {formatExactDatetime(device.last_heartbeat_at)}
        </Tooltip>
      </td>
      <td className="w-16 px-4 py-2 text-sm text-center">
        {!isKiosk &&
          (isOffline || device.has_audio_output === null ? (
            '—'
          ) : device.has_audio_output ? (
            <span className="font-bold text-green-600">✓</span>
          ) : (
            <span className="font-bold text-red-500">✗</span>
          ))}
      </td>
      <td className="w-28 px-4 py-2 font-mono text-sm text-gray-600">
        <span className="flex items-center gap-1.5">
          {device.app_version || '—'}
          {outdated && (
            <>
              <span
                data-for={`outdated-${device.id}`}
                data-tip
                className="flex items-center gap-1 text-red-500 cursor-default"
              >
                <ClockAlert14 />
                {formatOutdatedFor(device.outdated_since)}
              </span>
              <Tooltip id={`outdated-${device.id}`} effect="solid">
                {device.outdated_since
                  ? `Outdated since ${formatExactDatetime(device.outdated_since)}`
                  : 'Outdated'}
                {` · latest ${latestVersionFor(device, latestVersion)}`}
              </Tooltip>
            </>
          )}
        </span>
      </td>
      <td className="w-20 px-4 py-2 text-sm text-gray-400">
        {OS_LABEL[device.os_platform] ?? (device.os_platform || '—')}
      </td>
      <td className="w-36 px-4 py-2 font-mono text-sm text-gray-400">{device.ip_address || '—'}</td>
      <td className="w-28 px-4 py-2 text-sm">
        {canRefresh && (
          <button
            type="button"
            onClick={() => mutateRefresh()}
            disabled={isRefreshing}
            className="hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border rounded"
            aria-label="Refresh device"
          >
            <RefreshCw16 />
            Refresh
          </button>
        )}
      </td>
    </tr>
  );
};

const WLDevices: React.FC = () => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(FILTER_ALL);
  const [searchQuery, setSearchQuery] = useState('');
  const [countdown, setCountdown] = useState(REFETCH_INTERVAL_S);

  const { data, isLoading, isError, refetch } = useQuery('wlDevices', fetchWLDevices, {
    refetchOnWindowFocus: false,
  });

  const handleRefetch = useCallback(() => {
    refetch();
    setCountdown(REFETCH_INTERVAL_S);
  }, [refetch]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          handleRefetch();
          return REFETCH_INTERVAL_S;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [handleRefetch]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter]);

  const allDevices = useMemo<WLDevice[]>(() => data?.data?.results ?? [], [data]);

  const searched = useMemo(() => {
    const raw = searchQuery.trim();
    if (!raw) return allDevices;
    // Strip combining diacritics (U+0300-U+036F) after NFD decomposition
    const normalize = (s: string): string =>
      s
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    const q = normalize(raw);
    return allDevices.filter(
      (d) =>
        normalize(d.account_name).includes(q) ||
        (d.parent_account_name ? normalize(d.parent_account_name).includes(q) : false)
    );
  }, [allDevices, searchQuery]);

  const latestVersion = useMemo(() => getLatestVersion(allDevices), [allDevices]);

  const outdatedDevices = useMemo(
    () => searched.filter((d) => isOutdated(d, latestVersion)),
    [searched, latestVersion]
  );
  const outdatedCount = outdatedDevices.length;

  const filtered = useMemo(() => {
    if (statusFilter === FILTER_ALL) return searched;
    if (statusFilter === FILTER_OUTDATED) return outdatedDevices;
    return searched.filter((d) => getEffectiveStatus(d) === statusFilter);
  }, [searched, statusFilter, outdatedDevices]);

  const grouped = useMemo(() => groupDevices(filtered), [filtered]);

  const pageCount = Math.max(1, Math.ceil(grouped.length / GROUPS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const pagedGroups = grouped.slice(
    (currentPage - 1) * GROUPS_PER_PAGE,
    currentPage * GROUPS_PER_PAGE
  );
  const hasPrevPage = currentPage > 1;
  const hasNextPage = currentPage < pageCount;

  const counts = useMemo(() => {
    const acc: Record<DeviceStatus, number> = { online: 0, warning: 0, offline: 0 };
    searched.forEach((d) => {
      acc[getEffectiveStatus(d)] += 1;
    });
    return acc;
  }, [searched]);

  if (isLoading) return <DotBricksLoading />;

  if (isError)
    return (
      <div className="sm:px-6 lg:px-8 pt-8">
        <p className="text-red-600">Failed to load device data. Please try again.</p>
      </div>
    );

  return (
    <div className="sm:px-6 lg:px-8 h-full pt-8 pb-12 overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Waiting List Devices</h2>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span>Refreshing in {countdown}s</span>
          <button
            type="button"
            onClick={handleRefetch}
            className="hover:bg-gray-50 px-3 py-1 text-sm border rounded"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-5">
        <div className="inline-flex overflow-hidden text-sm border border-gray-200 divide-x divide-gray-200 rounded-full">
          {STATUS_BADGE_CONFIG.map(({ key, bg, text, dot }) => (
            <span key={key} className={`inline-flex items-center gap-1.5 ${bg} ${text} px-3 py-1`}>
              <span className={`inline-block w-2 h-2 ${dot} rounded-full`} />
              {counts[key]} {key}
            </span>
          ))}
        </div>
        {outdatedCount > 0 && (
          <button
            type="button"
            onClick={() =>
              setStatusFilter(statusFilter === FILTER_OUTDATED ? FILTER_ALL : FILTER_OUTDATED)
            }
            className={`inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full transition-colors ${
              statusFilter === FILTER_OUTDATED
                ? 'bg-orange-100 text-orange-800 border-2 border-orange-500 font-medium'
                : 'bg-orange-100 text-orange-800 border border-orange-200 hover:bg-orange-200'
            }`}
          >
            {outdatedCount} outdated
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 mb-5">
        {([FILTER_ALL, 'online', 'warning', 'offline'] as StatusFilter[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded text-sm border transition-colors ${
              statusFilter === s
                ? 'bg-primary-2.0 text-white border-primary-2.0'
                : 'bg-white border-gray-300 hover:bg-gray-50'
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <input
          type="search"
          placeholder="Search by account..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="ml-2 border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-2.0 w-52"
        />
        {statusFilter === FILTER_OUTDATED && (
          <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-800 border border-orange-200 text-sm px-2.5 py-1 rounded-full">
            Outdated
            <button
              type="button"
              onClick={() => setStatusFilter(FILTER_ALL)}
              className="hover:text-orange-900 ml-0.5"
              aria-label="Clear outdated filter"
            >
              <X16 />
            </button>
          </span>
        )}
      </div>

      {allDevices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <p className="text-base font-medium">No devices registered yet</p>
          <p className="mt-1 text-sm">
            Devices will appear here once they send their first heartbeat.
          </p>
        </div>
      ) : grouped.length === 0 ? (
        <p className="text-sm text-gray-500">No devices match the current filters.</p>
      ) : (
        <>
          {pagedGroups.map((parentGroup) => (
            <div key={parentGroup.parentAccountId ?? '__none__'} className="mb-8">
              {parentGroup.parentAccountName && (
                <h3 className="flex items-center gap-2 mb-3 text-base font-semibold text-gray-700">
                  <span className="w-3 h-3 rounded-full bg-primary-2.0 inline-block" />
                  {parentGroup.parentAccountName}
                </h3>
              )}

              {parentGroup.accounts.map((accountEntry) => (
                <div key={accountEntry.accountId} className="mb-5 ml-4">
                  <h4 className="mb-2 text-sm font-medium text-gray-600">
                    {accountEntry.accountName}
                  </h4>
                  <div className="overflow-x-auto border border-gray-200 rounded shadow-sm">
                    <table className="min-w-full text-left table-fixed">
                      <thead className="bg-gray-50 text-xs tracking-wider text-gray-500 uppercase">
                        <tr>
                          <th className="w-24 px-4 py-2">Type</th>
                          <th className="px-4 py-2">Name</th>
                          <th className="w-24 px-4 py-2">Status</th>
                          <th className="w-28 px-4 py-2">Last Seen</th>
                          <th className="w-16 px-4 py-2 text-center">Audio</th>
                          <th className="w-28 px-4 py-2">Version</th>
                          <th className="w-20 px-4 py-2">OS</th>
                          <th className="w-36 px-4 py-2">IP</th>
                          <th className="w-28 px-4 py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {accountEntry.devices.map((device) => (
                          <DeviceRow
                            key={device.id}
                            device={device}
                            latestVersion={latestVersion}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ))}
          <div className="flex items-center justify-between mt-6 text-sm text-gray-600">
            <span>
              Page {currentPage} of {pageCount} · {filtered.length} devices
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => p - 1)}
                disabled={!hasPrevPage}
                className="hover:bg-gray-50 disabled:opacity-50 px-3 py-1 border rounded"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasNextPage}
                className="hover:bg-gray-50 disabled:opacity-50 px-3 py-1 border rounded"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WLDevices;
